/**
 * Per-class selection caps (user order 2026-08-01):
 * - at most 4 measurement (trend-line) series on the chart;
 * - at most 4 ribbon overlays (conditions + medication courses) stacked
 *   above it;
 * - at most 4 in-plot event markers.
 * Clicking past a cap is refused with a warning naming the limit hit.
 */

import type { PlotClass } from './types';

export const MAX_NUMERIC_SERIES = 4;
export const MAX_RIBBON_OVERLAYS = 4;
export const MAX_EVENT_MARKERS = 4;

/** Which cap-group a plot class counts against. */
export function capGroup(plotClass: PlotClass): 'numeric' | 'ribbon' | 'event' {
  if (plotClass === 'numeric-series') return 'numeric';
  if (plotClass === 'event') return 'event';
  return 'ribbon'; // condition-range + course-range share the ribbon lanes
}

const CAPS: Record<ReturnType<typeof capGroup>, { max: number; message: string }> = {
  numeric: {
    max: MAX_NUMERIC_SERIES,
    message: `Up to ${MAX_NUMERIC_SERIES} measurement lines can be plotted at once — remove one to add another.`,
  },
  ribbon: {
    max: MAX_RIBBON_OVERLAYS,
    message: `Up to ${MAX_RIBBON_OVERLAYS} condition/medication ribbons can be shown at once — remove one to add another.`,
  },
  event: {
    max: MAX_EVENT_MARKERS,
    message: `Up to ${MAX_EVENT_MARKERS} event markers can be shown at once — remove one to add another.`,
  },
};

/**
 * The warning to show if adding an item of `plotClass` would exceed its cap,
 * given the plot classes already selected. Null when the add is allowed.
 */
export function selectionCapMessage(
  plotClass: PlotClass,
  selectedClasses: PlotClass[],
): string | null {
  const group = capGroup(plotClass);
  const current = selectedClasses.filter((c) => capGroup(c) === group).length;
  return current >= CAPS[group].max ? CAPS[group].message : null;
}
